import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, LockOpen, RefreshCw, Loader2, ExternalLink, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Report OTP lockouts. The cool-off clears itself, so an empty table is the
// normal state — this screen exists for the customer who phones in mid-wait.
export const ReportLockoutPanel = () => {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ cool_off_minutes: 15, max_attempts: 5, locked_now: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/report-lockouts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(data.lockouts || []);
      setMeta({
        cool_off_minutes: data.cool_off_minutes,
        max_attempts: data.max_attempts,
        locked_now: data.locked_now,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load lockouts');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const unlock = async (row) => {
    setBusy(`${row.report_id}:${row.phone}`);
    try {
      const { data } = await axios.post(`${API_URL}/api/report-lockouts/unlock`,
        { report_id: row.report_id, phone: row.phone },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(data.message || 'Unlocked');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not unlock');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="report-lockout-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Report Lockouts</h2>
          <p className="text-sm text-gray-500 max-w-2xl">
            A customer who types {meta.max_attempts} wrong codes waits {meta.cool_off_minutes} minutes
            before trying again. The wait clears itself — release someone early only when they are on
            the phone with you.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="lockout-refresh-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Lock className="h-4 w-4 text-amber-600" />
            Waiting out a cool-off
            <span className="ml-1 text-sm font-normal text-gray-500" data-testid="lockout-count">
              {meta.locked_now || 0} right now
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400" data-testid="lockout-empty">
              <ShieldCheck className="h-10 w-10 mb-2 text-emerald-200" />
              <p className="text-sm">Nobody is locked out of a report.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="py-2 pr-4 font-medium">Customer</th>
                    <th className="py-2 pr-4 font-medium">Phone</th>
                    <th className="py-2 pr-4 font-medium">Report</th>
                    <th className="py-2 pr-4 font-medium">Wrong tries</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const key = `${r.report_id}:${r.phone}`;
                    return (
                      <tr key={key} className="border-b last:border-0 hover:bg-gray-50"
                        data-testid={`lockout-row-${r.report_id}`}>
                        <td className="py-3 pr-4 font-medium text-gray-900">
                          {r.customer_name}
                          {r.car_number && <span className="block text-xs text-gray-400">{r.car_number}</span>}
                        </td>
                        <td className="py-3 pr-4 text-gray-700">{r.phone}</td>
                        <td className="py-3 pr-4">
                          <a href={r.report_url} target="_blank" rel="noreferrer"
                            className="text-blue-600 hover:underline inline-flex items-center gap-1">
                            {r.report_id}<ExternalLink className="h-3 w-3" />
                          </a>
                        </td>
                        <td className="py-3 pr-4 text-gray-700">{r.attempts}</td>
                        <td className="py-3 pr-4">
                          {r.is_locked_now ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                              <Lock className="h-3 w-3" /> {r.minutes_left} min left
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                              cleared
                            </span>
                          )}
                        </td>
                        <td className="py-3 text-right">
                          <Button size="sm" variant={r.is_locked_now ? 'default' : 'outline'}
                            onClick={() => unlock(r)} disabled={busy === key}
                            data-testid={`lockout-unlock-${r.report_id}`}>
                            {busy === key
                              ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
                              : <LockOpen className="h-4 w-4 mr-1" />}
                            Unlock
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportLockoutPanel;
